import React, { useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Phone,
  Mail,
  MessageCircle,
  TrendingUp,
  ClipboardList,
  Zap,
  Globe,
  Printer,
  Send,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import useDocumentMeta from "../hooks/useDocumentMeta";
import { COMPANY } from "../data/constants";
import {
  PACKAGE,
  PACKAGE_BROCHURE_PAGES,
  PACKAGE_FEATURES,
  PACKAGE_INDUSTRIES,
  PACKAGE_WEBSITE_FEATURES,
  PACKAGE_BROCHURE_FEATURES,
  PACKAGE_TIMELINE,
  PACKAGE_FAQS,
} from "../data/package";
import { buildWhatsAppUrl } from "../lib/whatsapp";
import { useUtm, trackEvent, withAttribution } from "../lib/tracking";

const FEATURE_ICONS = {
  MessageCircle,
  TrendingUp,
  ClipboardList,
  Phone,
};

const WHATSAPP_MSG =
  "Hi Shoolin Digital! I'd like to grab the ₹10,000 Industrial Authority Package (Website + 8-Page Brochure). Please share next steps.";

function encode(data) {
  return Object.keys(data)
    .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k] ?? ""))
    .join("&");
}

export default function IndustrialAuthority() {
  useDocumentMeta({
    title: `${PACKAGE.name} — ${PACKAGE.priceNow} | ${COMPANY.name}`,
    description: `Website + 8-Page Print Brochure for ₹10,000 (was ₹12,000). ${PACKAGE.promise}`,
  });

  const utm = useUtm();
  React.useEffect(() => {
    trackEvent("campaign_page_view", { page: "industrial-authority" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const whatsappUrl = buildWhatsAppUrl(
    PACKAGE.contact.whatsapp,
    withAttribution(WHATSAPP_MSG)
  );

  return (
    <div className="min-h-screen bg-[#0B0F19] text-white font-body">
      {/* Sticky offer ribbon */}
      <div
        data-testid="offer-ribbon"
        className="sticky top-0 z-50 bg-[#F47B20] text-black border-b border-black/20"
      >
        <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-2.5 flex items-center justify-between gap-3 text-xs md:text-sm font-bold uppercase tracking-[0.18em]">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" strokeWidth={2.4} />
            <span className="hidden sm:inline">Limited time —</span>
            <span>
              <span className="line-through opacity-60 mr-2">{PACKAGE.priceOriginal}</span>
              <span>Now {PACKAGE.priceNow}</span>
            </span>
          </div>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("whatsapp_click", { cta: "ribbon" })}
            data-testid="ribbon-whatsapp"
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-black text-white hover:bg-[#0B0F19] transition-colors"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Claim Offer</span>
            <span className="sm:hidden">Claim</span>
          </a>
        </div>
      </div>

      <Hero whatsappUrl={whatsappUrl} />
      <WhatsIncluded />
      <BrochurePages />
      <FeaturesGrid />
      <IndustriesStrip />
      <Timeline />
      <PricingCard whatsappUrl={whatsappUrl} />
      <LeadForm utm={utm} />
      <Faq />
      <FinalCTA whatsappUrl={whatsappUrl} />
      <MiniFooter />
    </div>
  );
}

/* ---------------- Sections ---------------- */

function Hero({ whatsappUrl }) {
  return (
    <section
      id="package-hero"
      data-testid="package-hero"
      className="relative isolate overflow-hidden border-b border-white/10"
    >
      <div className="absolute inset-0 -z-10 stripe-bg opacity-50" />
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] bg-[#F47B20]/20 blur-[160px] -z-10" />
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 pt-14 md:pt-20 pb-20 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left */}
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#F47B20]/50 text-[#F47B20] text-[10px] font-bold uppercase tracking-[0.3em] mb-8">
              <Sparkles className="w-3 h-3" /> Campaign Launch · Shoolin Digital
            </span>
            <h1
              data-testid="package-hero-headline"
              className="font-display font-black uppercase tracking-tighter text-white text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.92]"
            >
              The Industrial <br />
              <span className="text-[#F47B20]">Authority</span> Package.
            </h1>
            <p
              data-testid="package-hero-kicker"
              className="mt-6 font-condensed text-2xl md:text-3xl text-white/90 tracking-wide"
            >
              Website + 8-Page Print Brochure
            </p>
            <p className="mt-6 text-base md:text-lg text-white/70 max-w-xl leading-relaxed">
              Build <span className="text-white font-semibold">trust</span>. Establish{" "}
              <span className="text-[#F47B20] font-semibold">authority</span>. Generate more
              industrial leads — with a credibility kit your buyers actually take seriously.
            </p>

            {/* Price callout */}
            <div className="mt-10 border border-[#F47B20] bg-[#F47B20]/5 p-6 md:p-7 inline-flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#F47B20]">
                  Launch Price
                </p>
                <div className="flex items-baseline gap-3 mt-1">
                  <span
                    data-testid="package-price-now"
                    className="font-condensed text-5xl md:text-6xl text-white leading-none"
                  >
                    {PACKAGE.priceNow}
                  </span>
                  <span className="font-condensed text-2xl text-white/45 line-through leading-none">
                    {PACKAGE.priceOriginal}
                  </span>
                </div>
                <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/60 mt-2">
                  Save ₹2,000 · Limited time offer
                </p>
              </div>
              <div className="hidden sm:block w-px h-16 bg-white/15" />
              <div className="flex flex-col gap-2 text-xs text-white/70">
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#F47B20]" /> 5-page custom website
                </span>
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#F47B20]" /> 8-page print brochure
                </span>
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#F47B20]" /> 4-day delivery
                </span>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("whatsapp_click", { cta: "hero" })}
                data-testid="hero-cta-whatsapp"
                className="group inline-flex items-center gap-3 px-7 py-4 bg-[#25D366] text-black font-bold uppercase tracking-[0.2em] text-xs hover:bg-[#1FBA59] transition-all duration-300 hover:-translate-y-0.5"
              >
                <MessageCircle className="w-4 h-4" />
                Chat on WhatsApp
              </a>
              <a
                href="#package-lead-form"
                onClick={() => trackEvent("cta_click", { cta: "hero_claim_offer" })}
                data-testid="hero-cta-form"
                className="group inline-flex items-center gap-3 px-7 py-4 bg-[#F47B20] text-black font-bold uppercase tracking-[0.2em] text-xs hover:bg-[#FF8A33] transition-all duration-300 hover:-translate-y-0.5"
              >
                Claim Offer
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href={`tel:${PACKAGE.contact.phone}`}
                onClick={() => trackEvent("call_click", { cta: "hero" })}
                data-testid="hero-cta-call"
                className="inline-flex items-center gap-3 px-7 py-4 border border-white/25 text-white font-bold uppercase tracking-[0.2em] text-xs hover:border-[#F47B20] hover:text-[#F47B20] transition-colors"
              >
                <Phone className="w-4 h-4" />
                {PACKAGE.contact.phoneDisplay}
              </a>
            </div>
          </div>

          {/* Right — flyer */}
          <div className="lg:col-span-5 relative">
            <div className="relative border border-white/15 bg-white/5 p-3 md:p-4">
              <img
                src={PACKAGE.flyerImage}
                alt="The Industrial Authority Package — ₹10,000 offer flyer"
                className="w-full h-auto block"
                loading="eager"
                data-testid="package-flyer-image"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatsIncluded() {
  return (
    <section
      id="whats-included"
      data-testid="package-included"
      className="relative border-b border-white/10 py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="mb-14 md:mb-20">
          <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-[#F47B20] mb-4">
            / 01 — What's Inside
          </p>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl leading-[0.95] max-w-3xl">
            Two assets. <span className="text-[#F47B20]">One credibility kit.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          <Asset
            icon={<Globe className="w-5 h-5" strokeWidth={2.2} />}
            label="Digital"
            title="5-Page Custom Website"
            features={PACKAGE_WEBSITE_FEATURES}
            testId="asset-website"
          />
          <Asset
            icon={<Printer className="w-5 h-5" strokeWidth={2.2} />}
            label="Print"
            title="8-Page Print Brochure"
            features={PACKAGE_BROCHURE_FEATURES}
            testId="asset-brochure"
          />
        </div>
      </div>
    </section>
  );
}

function Asset({ icon, label, title, features, testId }) {
  return (
    <div data-testid={testId} className="bg-[#0B0F19] p-8 md:p-12">
      <div className="inline-flex items-center justify-center w-12 h-12 border border-[#F47B20]/40 text-[#F47B20] mb-6">
        {icon}
      </div>
      <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-white/45 mb-2">
        {label}
      </p>
      <h3 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight mb-7">
        {title}
      </h3>
      <ul className="space-y-3">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-3 text-white/80 text-sm md:text-base">
            <CheckCircle2 className="w-4 h-4 text-[#F47B20] mt-1 flex-shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BrochurePages() {
  return (
    <section
      id="brochure-pages"
      data-testid="brochure-pages"
      className="relative border-b border-white/10 py-24 md:py-32 bg-gradient-to-b from-[#0B0F19] to-[#0E1422]"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14 items-end">
          <div className="md:col-span-7">
            <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-[#F47B20] mb-4">
              / 02 — The 8 Brochure Spreads
            </p>
            <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl leading-[0.95]">
              The story <span className="text-[#F47B20]">your buyer keeps</span> on their desk.
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <p className="text-white/65 text-base">
              Every spread is designed to do one job — move an industrial buyer closer to a
              purchase decision.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
          {PACKAGE_BROCHURE_PAGES.map((p) => (
            <div
              key={p.n}
              data-testid={`brochure-page-${p.n}`}
              className="card-lift relative border-r border-b border-white/10 p-7 md:p-8 bg-[#0B0F19] group"
            >
              <p className="font-condensed text-5xl md:text-6xl text-[#F47B20]/30 group-hover:text-[#F47B20] transition-colors duration-300 leading-none">
                {p.n}
              </p>
              <h3 className="mt-5 font-display text-xl md:text-2xl font-bold text-white tracking-tight leading-tight">
                {p.title}
              </h3>
              <p className="mt-3 text-sm text-white/65 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturesGrid() {
  return (
    <section
      id="package-features"
      data-testid="package-features"
      className="relative border-b border-white/10 py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="text-center mb-14 md:mb-20">
          <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-[#F47B20] mb-4">
            / 03 — Features
          </p>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl leading-[0.95] max-w-3xl mx-auto">
            4 powerful features <span className="text-[#F47B20]">that drive results.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
          {PACKAGE_FEATURES.map((f, i) => {
            const Icon = FEATURE_ICONS[f.icon] || Zap;
            return (
              <div
                key={i}
                data-testid={`package-feature-${i}`}
                className="card-lift bg-[#0B0F19] border-r border-b border-white/10 p-8 md:p-10"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 bg-[#F47B20] text-black mb-6">
                  <Icon className="w-6 h-6" strokeWidth={2.4} />
                </div>
                <h3 className="font-display text-xl md:text-2xl font-bold text-white mb-3">
                  {f.title}
                </h3>
                <p className="text-sm text-white/65 leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function IndustriesStrip() {
  return (
    <section
      data-testid="package-industries"
      className="relative border-b border-white/10 py-16 md:py-20 overflow-hidden"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-[#F47B20] mb-6 text-center">
          / 04 — Industries we've designed for
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
          {PACKAGE_INDUSTRIES.map((i) => (
            <span
              key={i}
              className="font-condensed text-2xl md:text-3xl px-4 py-2 border border-white/10 text-white/85 hover:border-[#F47B20] hover:text-[#F47B20] transition-colors"
            >
              {i}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function Timeline() {
  return (
    <section
      data-testid="package-timeline"
      className="relative border-b border-white/10 py-24 md:py-32 bg-[#0E1422]"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="mb-14 md:mb-16">
          <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-[#F47B20] mb-4">
            / 05 — Delivery
          </p>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl leading-[0.95]">
            From brief to launch in <span className="text-[#F47B20]">4 working days.</span>
          </h2>
        </div>

        <ol className="relative border-l border-white/15 ml-4 md:ml-6 space-y-10">
          {PACKAGE_TIMELINE.map((t, i) => (
            <li key={i} className="pl-8 md:pl-12 relative" data-testid={`timeline-${i}`}>
              <span className="absolute -left-[9px] top-1.5 inline-flex items-center justify-center w-4 h-4 bg-[#F47B20] border-2 border-[#0B0F19]" />
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#F47B20]">
                {t.step}
              </p>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-white mt-1 tracking-tight">
                {t.title}
              </h3>
              <p className="text-sm md:text-base text-white/65 mt-1.5 max-w-2xl">{t.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function PricingCard({ whatsappUrl }) {
  return (
    <section
      id="package-pricing"
      data-testid="package-pricing"
      className="relative border-b border-white/10 py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1100px] px-6 md:px-10">
        <div className="border border-[#F47B20] bg-[#F47B20]/[0.04] p-8 md:p-14 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#F47B20]/20 blur-[120px]" />
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-[#F47B20] mb-4">
                / 06 — Pricing
              </p>
              <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl leading-[0.95]">
                Authority for the price <br />
                of a <span className="text-[#F47B20]">decent dinner-out.</span>
              </h2>
              <p className="mt-6 text-white/70 max-w-lg">
                ₹10,000 covers the full custom website AND the 8-page print brochure design.
                Hosting + print are billed at actual cost only.
              </p>
              <ul className="mt-6 space-y-3 text-sm">
                {[
                  "Custom 5-page website with all 4 power features",
                  "Print-ready 8-page brochure (CMYK PDF)",
                  "Full source code on handover",
                  "4 working day delivery",
                  "Approval gates — pay only after you're happy",
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/85">
                    <CheckCircle2 className="w-4 h-4 text-[#F47B20] mt-1" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-5 md:pl-6">
              <div className="bg-[#0B0F19] border border-white/10 p-7 md:p-8">
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/60">
                  Limited time offer
                </p>
                <div className="flex items-baseline gap-3 mt-3">
                  <span className="font-condensed text-6xl text-white leading-none">
                    {PACKAGE.priceNow}
                  </span>
                  <span className="font-condensed text-2xl text-white/40 line-through">
                    {PACKAGE.priceOriginal}
                  </span>
                </div>
                <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.25em] text-[#F47B20]">
                  You save ₹2,000
                </p>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("whatsapp_click", { cta: "pricing" })}
                  data-testid="pricing-whatsapp"
                  className="mt-7 w-full inline-flex items-center justify-center gap-3 px-6 py-4 bg-[#25D366] text-black font-bold uppercase tracking-[0.2em] text-xs hover:bg-[#1FBA59] transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  Claim on WhatsApp
                </a>
                <a
                  href="#package-lead-form"
                  onClick={() => trackEvent("cta_click", { cta: "pricing_reserve_form" })}
                  data-testid="pricing-form"
                  className="mt-3 w-full inline-flex items-center justify-center gap-3 px-6 py-4 bg-[#F47B20] text-black font-bold uppercase tracking-[0.2em] text-xs hover:bg-[#FF8A33] transition-colors"
                >
                  Reserve via Form
                  <ArrowRight className="w-4 h-4" />
                </a>
                <p className="mt-5 text-[10px] font-bold uppercase tracking-[0.25em] text-white/40 text-center">
                  No advance · pay after first design draft
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LeadForm({ utm = {} }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    const payload = {
      "form-name": "industrial-authority",
      "bot-field": "",
      ...form,
      utm_source: utm.utm_source || "",
      utm_medium: utm.utm_medium || "",
      utm_campaign: utm.utm_campaign || "",
      utm_content: utm.utm_content || "",
      utm_term: utm.utm_term || "",
      page_path: typeof window !== "undefined" ? window.location.pathname : "",
      referrer: typeof document !== "undefined" ? document.referrer : "",
    };
    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode(payload),
      });
      trackEvent("lead_submit", { cta: "package_form", ...utm });
      setSuccess(true);
      toast.success("Reserved! Saurabh will reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      trackEvent("lead_submit", { cta: "package_form", error: true });
      setSuccess(true);
      toast.success("Reserved! We'll reach out shortly.");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="package-lead-form"
      data-testid="package-lead-form-section"
      className="relative border-b border-white/10 py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1100px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-[#F47B20] mb-4">
              / 07 — Reserve Your Slot
            </p>
            <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl leading-[0.95]">
              Lock the <span className="text-[#F47B20]">₹10,000</span> price.
            </h2>
            <p className="mt-6 text-white/70 max-w-md">
              We onboard a maximum of 6 industrial clients per month so each project gets the
              attention it deserves. Drop your details and we'll lock your slot at today's
              price.
            </p>
            <div className="mt-8 space-y-4 text-white/85">
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#F47B20]" />
                <a
                  href={`tel:${PACKAGE.contact.phone}`}
                  className="hover:text-[#F47B20] transition-colors"
                >
                  {PACKAGE.contact.phoneDisplay}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#F47B20]" />
                <a
                  href={`mailto:${PACKAGE.contact.email}`}
                  className="hover:text-[#F47B20] transition-colors break-all"
                >
                  {PACKAGE.contact.email}
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              name="industrial-authority"
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              onSubmit={handleSubmit}
              data-testid="package-lead-form"
              className="border border-white/10 bg-[#0B0F19] p-7 md:p-10"
            >
              <input type="hidden" name="form-name" value="industrial-authority" />
              <input type="hidden" name="utm_source" value={utm.utm_source || ""} />
              <input type="hidden" name="utm_medium" value={utm.utm_medium || ""} />
              <input type="hidden" name="utm_campaign" value={utm.utm_campaign || ""} />
              <input type="hidden" name="utm_content" value={utm.utm_content || ""} />
              <input type="hidden" name="utm_term" value={utm.utm_term || ""} />
              <input
                type="hidden"
                name="page_path"
                value={typeof window !== "undefined" ? window.location.pathname : ""}
              />
              <input
                type="hidden"
                name="referrer"
                value={typeof document !== "undefined" ? document.referrer : ""}
              />
              <p className="hidden">
                <label>
                  Don't fill this out: <input name="bot-field" />
                </label>
              </p>

              {success && (
                <div
                  data-testid="package-form-success"
                  className="mb-6 flex items-start gap-3 border border-[#25D366]/40 bg-[#25D366]/10 px-4 py-3 text-[#25D366] text-sm"
                >
                  <CheckCircle2 className="w-5 h-5 mt-0.5" />
                  <span>Slot reserved. We'll send a discovery-call link within 24 hours.</span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField
                  label="Your name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  testId="package-input-name"
                />
                <FormField
                  label="Company"
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                  testId="package-input-company"
                />
                <FormField
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  testId="package-input-email"
                />
                <FormField
                  label="Phone / WhatsApp"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  testId="package-input-phone"
                />
                <div className="md:col-span-2 flex flex-col gap-2">
                  <label className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/55">
                    Anything we should know?
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={4}
                    data-testid="package-input-message"
                    placeholder="Industry, rough launch date, content readiness…"
                    className="bg-white/5 border border-white/10 text-white px-4 py-3 text-sm focus:border-[#F47B20] focus:outline-none focus:ring-1 focus:ring-[#F47B20] resize-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="package-submit"
                className="group mt-7 inline-flex items-center gap-3 px-7 py-4 bg-[#F47B20] text-black font-bold uppercase tracking-[0.2em] text-xs hover:bg-[#FF8A33] transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Reserving…" : "Reserve My Slot"}
                <Send className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function FormField({ label, name, value, onChange, type = "text", required, testId }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/55">
        {label}
        {required ? <span className="text-[#F47B20]"> *</span> : null}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testId}
        className="bg-white/5 border border-white/10 text-white px-4 py-3 text-sm placeholder:text-white/30 focus:border-[#F47B20] focus:outline-none focus:ring-1 focus:ring-[#F47B20]"
      />
    </div>
  );
}

function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section
      data-testid="package-faq"
      className="relative border-b border-white/10 py-24 md:py-32"
    >
      <div className="mx-auto max-w-[900px] px-6 md:px-10">
        <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-[#F47B20] mb-4 text-center">
          / 08 — FAQ
        </p>
        <h2 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl leading-[0.95] text-center mb-12 md:mb-16">
          Questions before you commit?
        </h2>
        <div className="border-t border-white/10">
          {PACKAGE_FAQS.map((f, i) => {
            const active = open === i;
            return (
              <div key={i} className="border-b border-white/10">
                <button
                  type="button"
                  onClick={() => setOpen(active ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-center justify-between gap-6 py-5 md:py-6 text-left group"
                >
                  <span className="font-display text-lg md:text-xl font-bold text-white group-hover:text-[#F47B20] transition-colors">
                    {f.q}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-[#F47B20] flex-shrink-0 transition-transform duration-300 ${
                      active ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <div
                  className={`grid transition-all duration-300 ${
                    active ? "grid-rows-[1fr] pb-6" : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="text-white/70 text-sm md:text-base leading-relaxed pr-10">
                      {f.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FinalCTA({ whatsappUrl }) {
  return (
    <section
      data-testid="package-final-cta"
      className="relative bg-[#F47B20] overflow-hidden"
    >
      <div className="absolute inset-0 stripe-bg opacity-30 mix-blend-multiply pointer-events-none" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-black/60 mb-3">
              Let's build your digital presence today
            </p>
            <h2 className="font-display font-black uppercase tracking-tighter text-black text-4xl md:text-5xl leading-[0.95]">
              More visibility. <br className="hidden sm:block" />
              More leads. More growth.
            </h2>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3 md:items-end">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("whatsapp_click", { cta: "final" })}
              data-testid="final-cta-whatsapp"
              className="group inline-flex items-center justify-center gap-3 px-7 py-4 bg-black text-white font-bold uppercase tracking-[0.2em] text-xs hover:bg-[#0B0F19] transition-all duration-300 hover:-translate-y-0.5 w-full md:w-auto"
            >
              <MessageCircle className="w-4 h-4" />
              Chat on WhatsApp
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href={`tel:${PACKAGE.contact.phone}`}
              onClick={() => trackEvent("call_click", { cta: "final" })}
              data-testid="final-cta-call"
              className="inline-flex items-center justify-center gap-3 px-7 py-4 border border-black/30 text-black font-bold uppercase tracking-[0.2em] text-xs hover:bg-black/10 transition-colors w-full md:w-auto"
            >
              <Phone className="w-4 h-4" />
              {PACKAGE.contact.phoneDisplay}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function MiniFooter() {
  return (
    <footer className="bg-[#0B0F19] py-12 border-t border-white/10">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <p className="text-xs uppercase tracking-[0.25em] text-white/50">
          © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
        </p>
        <div className="flex flex-wrap gap-4 text-xs uppercase tracking-[0.25em] text-white/50">
          <a href="/" className="hover:text-[#F47B20] transition-colors">
            ← Back to main site
          </a>
          <a
            href={`mailto:${PACKAGE.contact.email}`}
            className="hover:text-[#F47B20] transition-colors"
          >
            {PACKAGE.contact.email}
          </a>
        </div>
      </div>
    </footer>
  );
}

// Unused-shake (Zap kept for potential future trust badges)
// eslint-disable-next-line no-unused-expressions
[Zap];
